import { useState } from 'react'
import { Building2, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCollectionRuns,
  useCreateSupplier,
  useDeleteSupplier,
  useRunCollection,
  useSuppliers,
  useUpdateSupplier,
} from '@/features/parts/hooks'
import type { OrderingMethod, Supplier, SupplierWriteBody } from '@/types/parts'

const ORDERING_METHODS: { value: OrderingMethod; label: string }[] = [
  { value: 'email_po', label: 'Email PO' },
  { value: 'distributor_api', label: 'Distributor API' },
  { value: 'manual', label: 'Manual (no ordering)' },
]

const COLLECTION_SOURCES: { value: string; label: string }[] = [
  { value: '', label: 'None (import CSVs manually)' },
  { value: 'shopify', label: 'Shopify storefront (product JSON)' },
]

export function SuppliersTab() {
  const { data: suppliers = [], isLoading, isError } = useSuppliers()
  const createMutation = useCreateSupplier()
  const updateMutation = useUpdateSupplier()
  const deleteMutation = useDeleteSupplier()
  const runMutation = useRunCollection()
  const { data: runs = [] } = useCollectionRuns()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [deleting, setDeleting] = useState<Supplier | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleSave = (body: SupplierWriteBody) => {
    setFormError(null)
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, body },
        { onSuccess: () => setEditing(null), onError: (err) => setFormError(errMessage(err)) },
      )
    } else {
      createMutation.mutate(body, {
        onSuccess: () => setFormOpen(false),
        onError: (err) => setFormError(errMessage(err)),
      })
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" /> Parts suppliers
            </CardTitle>
            <CardDescription>
              Vendors the marina buys parts from. Suppliers with a collection source are refreshed
              automatically by the price collector; others are filled by CSV imports.
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Supplier
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="py-6 text-center text-sm text-destructive">Failed to load suppliers.</p>
          ) : suppliers.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No suppliers yet — add one to start the price book.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Ordering</TableHead>
                  <TableHead>Collection</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <span className="font-medium">{s.name}</span>
                      {s.website_url && (
                        <a
                          className="ml-2 text-xs text-muted-foreground hover:underline"
                          href={s.website_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {new URL(s.website_url).hostname}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {s.ordering_method.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {s.collection_enabled ? (
                        <span className="flex items-center gap-1.5 text-sm">
                          <RefreshCw className="h-3.5 w-3.5 text-emerald-600" />
                          {s.collection_source}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Off</span>
                      )}
                    </TableCell>
                    <TableCell>{s.active ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="text-right">
                      {s.collection_enabled && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Collect prices from ${s.name}`}
                          disabled={runMutation.isPending}
                          onClick={() => runMutation.mutate(s.id)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${s.name}`}
                        onClick={() => {
                          setEditing(s)
                          setFormOpen(true)
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        aria-label={`Delete ${s.name}`}
                        onClick={() => setDeleting(s)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent runs for the suppliers that have them */}
      {runs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent collection runs</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">New / Updated</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.slice(0, 8).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.supplier_name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.status === 'success'
                            ? 'default'
                            : r.status === 'partial'
                              ? 'secondary'
                              : 'destructive'
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {r.parts_created} / {r.parts_updated}
                    </TableCell>
                    <TableCell className="text-right">{r.errors}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit dialog */}
      <SupplierFormDialog
        open={formOpen}
        editing={editing}
        onOpenChange={(o) => {
          setFormOpen(o)
          if (!o) {
            setEditing(null)
            setFormError(null)
          }
        }}
        onSubmit={handleSave}
        isPending={createMutation.isPending || updateMutation.isPending}
        error={formError}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete supplier?</DialogTitle>
            <DialogDescription>
              Removes “{deleting?.name}”. Catalog parts keep their rows with no supplier attached;
              existing purchase orders are kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleting &&
                deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
              }
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function errMessage(err: unknown) {
  return err instanceof Error ? err.message : 'Request failed'
}

interface SupplierFormState {
  name: string
  website_url: string
  ordering_method: OrderingMethod
  ordering_email: string
  api_endpoint: string
  api_dealer_account: string
  collection_source: string
  max_pages: string
  delay_ms: string
  collection_enabled: boolean
  notes: string
  active: boolean
}

function supplierToForm(s: Supplier): SupplierFormState {
  return {
    name: s.name,
    website_url: s.website_url,
    ordering_method: s.ordering_method,
    ordering_email: s.ordering_email,
    api_endpoint: s.api_config?.endpoint ?? '',
    api_dealer_account: s.api_config?.dealer_account ?? '',
    collection_source: s.collection_source,
    max_pages: s.collection_config?.max_pages != null ? String(s.collection_config.max_pages) : '2',
    delay_ms: s.collection_config?.delay_ms != null ? String(s.collection_config.delay_ms) : '3000',
    collection_enabled: s.collection_enabled,
    notes: s.notes,
    active: s.active,
  }
}

function SupplierFormDialog({
  open,
  editing,
  onOpenChange,
  onSubmit,
  isPending,
  error,
}: {
  open: boolean
  editing: Supplier | null
  onOpenChange: (open: boolean) => void
  onSubmit: (body: SupplierWriteBody) => void
  isPending: boolean
  error: string | null
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* State lives here so it resets each time the dialog opens. */}
        <SupplierFormFields
          key={`${editing?.id ?? 'new'}-${open}`}
          initialValues={editing ? supplierToForm(editing) : emptySupplierForm()}
          isEdit={!!editing}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          isPending={isPending}
          error={error}
        />
      </DialogContent>
    </Dialog>
  )
}

function SupplierFormFields({
  initialValues,
  isEdit,
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  initialValues: SupplierFormState
  isEdit: boolean
  onSubmit: (body: SupplierWriteBody) => void
  onCancel: () => void
  isPending: boolean
  error: string | null
}) {
  const [form, setForm] = useState<SupplierFormState>(initialValues)

  const set = (patch: Partial<SupplierFormState>) => setForm({ ...form, ...patch })
  const int = (s: string) => (s && Number.isFinite(Number(s)) ? Number(s) : undefined)

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit supplier' : 'New supplier'}</DialogTitle>
        <DialogDescription>
          Ordering method controls how purchase orders reach this supplier; collection controls the
          automatic price collector.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-1">
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="sup-name">Name</Label>
            <Input
              id="sup-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sup-url">Website</Label>
            <Input
              id="sup-url"
              value={form.website_url}
              onChange={(e) => set({ website_url: e.target.value })}
              placeholder="https://…"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="sup-method">Ordering method</Label>
            <Select
              value={form.ordering_method}
              onValueChange={(v) => set({ ordering_method: v as OrderingMethod })}
            >
              <SelectTrigger id="sup-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDERING_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sup-email">Ordering email</Label>
            <Input
              id="sup-email"
              type="email"
              value={form.ordering_email}
              onChange={(e) => set({ ordering_email: e.target.value })}
            />
          </div>
        </div>

        {form.ordering_method === 'distributor_api' && (
          <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sup-api-endpoint">API endpoint</Label>
              <Input
                id="sup-api-endpoint"
                value={form.api_endpoint}
                onChange={(e) => set({ api_endpoint: e.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sup-api-account">Dealer account</Label>
              <Input
                id="sup-api-account"
                value={form.api_dealer_account}
                onChange={(e) => set({ api_dealer_account: e.target.value })}
              />
            </div>
            <p className="col-span-2 text-xs text-muted-foreground">
              Distributor API ordering is a stub until dealer credentials are provisioned;
              credentials themselves live in server env vars, never here.
            </p>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="sup-collection">Price collection</Label>
          <Select
            value={form.collection_source || 'none'}
            onValueChange={(v) => set({ collection_source: v === 'none' ? '' : v })}
          >
            <SelectTrigger id="sup-collection">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COLLECTION_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value || 'none'}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {form.collection_source !== '' && (
          <div className="grid grid-cols-3 items-end gap-3 rounded-md border p-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sup-maxpages">Max pages / run</Label>
              <Input
                id="sup-maxpages"
                type="number"
                min="1"
                value={form.max_pages}
                onChange={(e) => set({ max_pages: e.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sup-delay">Delay (ms)</Label>
              <Input
                id="sup-delay"
                type="number"
                min="1000"
                step="500"
                value={form.delay_ms}
                onChange={(e) => set({ delay_ms: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Checkbox
                id="sup-collect-enabled"
                checked={form.collection_enabled}
                onCheckedChange={(v) => set({ collection_enabled: v === true })}
              />
              <Label htmlFor="sup-collect-enabled" className="text-sm font-normal">
                Enabled
              </Label>
            </div>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="sup-notes">Notes</Label>
          <Input
            id="sup-notes"
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="sup-active"
            checked={form.active}
            onCheckedChange={(v) => set({ active: v === true })}
          />
          <Label htmlFor="sup-active" className="text-sm font-normal">
            Active
          </Label>
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!form.name || isPending}
          onClick={() =>
            onSubmit({
              name: form.name,
              website_url: form.website_url || undefined,
              ordering_method: form.ordering_method,
              ordering_email: form.ordering_email || undefined,
              api_config:
                form.ordering_method === 'distributor_api'
                  ? {
                      ...(form.api_endpoint ? { endpoint: form.api_endpoint } : {}),
                      ...(form.api_dealer_account
                        ? { dealer_account: form.api_dealer_account }
                        : {}),
                    }
                  : undefined,
              collection_source: form.collection_source,
              collection_config:
                form.collection_source !== ''
                  ? {
                      max_pages: int(form.max_pages) ?? 2,
                      delay_ms: int(form.delay_ms) ?? 3000,
                    }
                  : undefined,
              collection_enabled: form.collection_source !== '' && form.collection_enabled,
              notes: form.notes || undefined,
              active: form.active,
            })
          }
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Supplier'}
        </Button>
      </DialogFooter>
    </>
  )
}

function emptySupplierForm(): SupplierFormState {
  return {
    name: '',
    website_url: '',
    ordering_method: 'email_po',
    ordering_email: '',
    api_endpoint: '',
    api_dealer_account: '',
    collection_source: '',
    max_pages: '2',
    delay_ms: '3000',
    collection_enabled: true,
    notes: '',
    active: true,
  }
}
